import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, HStack, Tag, Text, VStack } from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchAllManualVerificationSessions } from '../../../redux/manualVerification/sessionsDetails/actions';
import { getAllManualVerificationSessions } from '../../../redux/manualVerification/sessionsDetails/selectors';
import {
  setSelectedAppId,
  setSelectedSessionId,
  updateActiveStep,
} from '../../../redux/manualVerification/stepperDetails';
import { getAppAnalytics } from '../../../redux/viewApp/appAnalytics/selectors';
import {
  statusTagColors,
  statusUpperCaseToLowerCase,
} from '../../../utils/manual-verification-session/manual-verification-constants';

export const AppManualVerificationMetrics = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const allManualVerificationSessions = useAppSelector(getAllManualVerificationSessions);
  const appAnalytics = useAppSelector(getAppAnalytics);

  useEffect(() => {
    dispatch(
      fetchAllManualVerificationSessions({
        appId: appAnalytics?.appDetail?.appId,
        queryType: 'ALL',
      })
    );
  }, []);

  function showNetworkRequestDump(sessionId: string) {
    dispatch(setSelectedSessionId(sessionId));
    dispatch(updateActiveStep(3));
    navigate(`/manual-verification/sessions/${sessionId}`);
  }

  function viewAllSessions() {
    dispatch(setSelectedAppId(appAnalytics?.appDetail?.appId));
    dispatch(updateActiveStep(2));
    navigate('/manual-verification');
  }

  return (
    <Box
      borderRadius="16px"
      mt="30px"
      w="50%"
      h="26vh"
      overflow="auto"
      bgColor="#F7F9FB"
      borderWidth="1px"
      boxShadow="2px"
    >
      <VStack align="flex-start" p={5} w="100%" h="100%" spacing={4}>
        <VStack w="100%" align="flex-start" h="10%">
          <HStack w="100%" justifyContent="space-between">
            <Text
              color="black"
              className="small-font"
              whiteSpace="pre-line"
              style={{ fontWeight: 650 }}
              fontWeight="bold"
              fontFamily="inter"
            >
              Manual Verification Request
            </Text>
            {!!allManualVerificationSessions?.length && (
              <Button
                className="x-small-font"
                variant="link"
                colorScheme="blackAlpha"
                _hover={{ color: 'black' }}
                textDecoration="underline"
                onClick={viewAllSessions}
              >
                <Text className="x-small-font" color="gray" textDecoration="underline">
                  view all
                </Text>
              </Button>
            )}
          </HStack>
        </VStack>
        <VStack w="100%" h="80%">
          <Box
            width="100%"
            h="100%"
            overflowY="auto"
            overflowX="auto"
            css={{
              '&::-webkit-scrollbar': {
                width: '8px',
                height: '8px',
                marginLeft: '10px',
              },
              '&::-webkit-scrollbar-track': {
                width: '6px',
                marginLeft: '10px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#CBD5E0',
                borderRadius: '24px',
                marginLeft: '10px',
              },
            }}
          >
            <VStack spacing={2}>
              {!allManualVerificationSessions?.length && (
                <Text
                  color="black"
                  fontFamily="inter"
                  className="x-small-font"
                  fontWeight={400}
                  textAlign="center"
                  mt={5}
                >
                  No Manual Verification Sessions Found
                </Text>
              )}
              {allManualVerificationSessions?.map((session) => (
                <VStack
                  alignItems="flex-start"
                  w="100%"
                  px={1}
                  pb={1}
                  key={session?.id}
                  borderWidth="0.5px"
                  borderColor="transparent"
                  cursor="pointer"
                  spacing={0}
                  onClick={() => {
                    showNetworkRequestDump(session?.sessionId);
                  }}
                  _hover={{
                    backgroundColor: '#F7F9FB',
                    borderRadius: 10,
                    borderColor: 'gray',
                    borderWidth: '0.5px',
                  }}
                >
                  <HStack w="100%" justifyContent="space-between">
                    <Text className="x-small-font" noOfLines={1} fontWeight={450}>
                      Provider Name - {session?.appName}
                    </Text>
                    <Tag
                      colorScheme={statusTagColors?.[session?.status]}
                      size="xs"
                      borderRadius={20}
                      px={2}
                      py={1}
                      variant="subtle"
                      className="x-small-tag-font"
                      textTransform="capitalize"
                    >
                      {statusUpperCaseToLowerCase?.[session?.status] ?? '-'}
                    </Tag>
                  </HStack>
                  <Text
                    className="xx-small-font"
                    color="rgba(0, 0, 0, 0.40)"
                    noOfLines={1}
                    fontFamily="Inter"
                  >
                    Session Id - {session?.sessionId}
                  </Text>
                  <Text
                    className="xx-small-font"
                    color="rgba(0, 0, 0, 0.40)"
                    noOfLines={2}
                  >
                    Extracted data -{' '}
                    {session?.parameters
                      .map((param) => `${param.key} : ${param.value}`)
                      .join(', ')}
                  </Text>
                </VStack>
                // </Flex>
              ))}
            </VStack>
          </Box>
        </VStack>
      </VStack>
    </Box>
  );
};
